import React from 'react';

/// USGS svg icons ///
function LakeIcon({ color = '#C4C4C4', border = '#000', ...rest }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" aria-hidden="true" {...rest}>
      <rect
        x="2"
        y="2"
        width="16"
        height="16"
        rx="1"
        fill={color}
        stroke={border}
        strokeWidth="1"
      />
    </svg>
  );
}

function StreamIcon({ color = '#C4C4C4', border = '#000', ...rest }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" aria-hidden="true" {...rest}>
      <circle cx="10" cy="10" r="8" fill={color} stroke={border} strokeWidth="1" />
    </svg>
  );
}

function WetlandIcon({ color = '#C4C4C4', border = '#000', ...rest }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="1 0 18 20" aria-hidden="true" {...rest}>
      <polygon
        points="17,1 17,19 2,10"
        fill={color}
        stroke={border}
        strokeWidth="1"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function EstuaryIcon({ color = '#C4C4C4', border = '#000', ...rest }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="1 0 18 20" aria-hidden="true" {...rest}>
      <polygon
        points="2,1 2,19 17,10"
        fill={color}
        stroke={border}
        strokeWidth="1"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CoastalIcon({ color = '#C4C4C4', border = '#000', ...rest }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 1 20 18" aria-hidden="true" {...rest}>
      <polygon
        points="1,3 19,3 10,17"
        fill={color}
        stroke={border}
        strokeWidth="1"
        strokeLinejoin="round"
      />
    </svg>
  );
}
/// End USGS svg icons ///

//Default shapes and colors for USGS devices and their states
const SHAPES = {
  lake: LakeIcon,
  stream: StreamIcon,
  wetland: WetlandIcon,
  estuary: EstuaryIcon,
  coastal: CoastalIcon,
};

const COLORS = {
  major: 'rgb(238, 170, 221)', // In major flood stage
  moderate: 'rgb(238, 153, 136)', // In moderate flood stage
  minor: 'rgb(255, 204, 136)', // In minor flood stage
  action: 'rgb(255, 255, 85)', // In action stage
  notFlooding: 'rgb(170, 238, 102)', // Not flooding
  noFloodStages: 'rgb(255, 255, 255)', // Flood stages not established
  measurement: 'rgb(153, 153, 153)', // Measurement flag
  unavailable: 'rgb(153, 153, 153)', // Recent measurement unavailable
};

const BORDER_COLOR = {
  major: 'rgb(221, 85, 204)', // In major flood stage
  moderate: 'rgb(221, 68, 51)', // In moderate flood stage
  minor: 'rgb(255, 153, 51)', // In minor flood stage
  action: 'rgb(255, 238, 0)', // In action stage
  notFlooding: 'rgb(119, 187, 34)', // Not flooding
  noFloodStages: 'rgb(102, 102, 102)', // Flood stages not established
  measurement: 'rgb(153, 102, 51)', // Measurement flag
  unavailable: 'rgb(0, 0, 0)', // Recent measurement unavailable
};
///End Default shapes and colors ///

//Typedefs for USGSMarker props

/**
 * @typedef {'lake'|'stream'|'wetland'|'estuary'|'coastal'} USGSMarkerType
 */

/**
 * @typedef {'major'|'moderate'|'minor'|'action'|'notFlooding'|'noFloodStages'|'measurement'|'unavailable'|string} USGSMarkerState
 */

/** @typedef {number} USGSMarkerSize */

/**
 * @typedef {Object<string, any>} USGSMarkerRest
 * Additional props forwarded to the underlying SVG element
 */

/**
 * USGSMarker
 *
 * Renders a USGS SVG marker. See the individual typedefs for prop types.
 *
 * @param {USGSMarkerType} type - The type of USGS feature (lake, stream, wetland, estuary, coastal)
 * @param {USGSMarkerState} state - The state of the USGS feature (major, moderate, minor, action, notFlooding, noFloodStages, measurement, unavailable)
 * @param {string} className - Additional class names for styling
 * @param {USGSMarkerRest} rest - Additional props to pass to the SVG element
 * @returns {JSX.Element} SVG Graphic representing the USGS marker
 */
export default function USGSMarker({
  type = 'stream',
  state = 'noFloodStages',
  className,
  ...rest
}) {
  const Icon = SHAPES[type] || StreamIcon;
  const resolvedColor = COLORS[state] || COLORS.unavailable;
  const resolvedBorder = BORDER_COLOR[state] || BORDER_COLOR.unavailable;
  return <Icon color={resolvedColor} border={resolvedBorder} className={className} {...rest} />;
}
